import { z } from 'zod';

/**
 * The complete permission catalogue.
 *
 * A permission is a verb on a resource. It answers "may this actor do X at
 * all?". *Which records* they may do it to is a separate question answered by
 * the scope attached to the permission in a role (see `Scope` below and
 * ADR-009). Keeping the two orthogonal is what lets us express "may edit any
 * ticket in their groups, but may only delete tickets assigned to them".
 *
 * Permission strings are stable API surface. Renaming one is a breaking change;
 * add a new one and deprecate instead.
 */

// ---------------------------------------------------------------------------
// Scope
// ---------------------------------------------------------------------------

/**
 * How wide a permission reaches. Ordered from broadest to narrowest — a role
 * granted `workspace` implicitly covers everything `group`, `assigned` and
 * `own` would have allowed within that workspace.
 */
export const SCOPES = ['global', 'workspace', 'group', 'assigned', 'own'] as const;
export const ScopeSchema = z.enum(SCOPES);
export type Scope = z.infer<typeof ScopeSchema>;

export const SCOPE_RANK: Record<Scope, number> = {
  global: 4,
  workspace: 3,
  group: 2,
  assigned: 1,
  own: 0,
};

/** True when `granted` is at least as wide as `required`. */
export function scopeCovers(granted: Scope, required: Scope): boolean {
  return SCOPE_RANK[granted] >= SCOPE_RANK[required];
}

export function widestScope(scopes: readonly Scope[]): Scope | undefined {
  return scopes.reduce<Scope | undefined>(
    (best, s) => (best === undefined || SCOPE_RANK[s] > SCOPE_RANK[best] ? s : best),
    undefined,
  );
}

// ---------------------------------------------------------------------------
// Catalogue
// ---------------------------------------------------------------------------

export const PERMISSIONS = {
  // --- Tickets -------------------------------------------------------------
  ticket: [
    'ticket.view',
    'ticket.create',
    'ticket.edit',
    'ticket.delete',
    'ticket.restore',
    'ticket.purge',
    'ticket.assign',
    'ticket.change_workspace',
    'ticket.reply',
    'ticket.forward',
    'ticket.note.view',
    'ticket.note.create',
    'ticket.merge',
    'ticket.split',
    'ticket.link',
    'ticket.bulk_edit',
    'ticket.export',
    'ticket.spam',
    'ticket.edit_requester',
    'ticket.reopen_closed',
    'ticket.time_entry.view',
    'ticket.time_entry.manage',
    'ticket.time_entry.manage_others',
    'ticket.task.view',
    'ticket.task.manage',
    'ticket.canned_response.use',
    'ticket.canned_response.manage',
  ],

  // --- ITSM practices ------------------------------------------------------
  problem: ['problem.view', 'problem.create', 'problem.edit', 'problem.delete', 'problem.assign'],
  change: [
    'change.view',
    'change.create',
    'change.edit',
    'change.delete',
    'change.assign',
    'change.approve',
    'change.manage_cab',
    'change.override_blackout',
  ],
  release: ['release.view', 'release.create', 'release.edit', 'release.delete', 'release.assign'],

  // --- Service catalogue and requests --------------------------------------
  catalog: [
    'catalog.view',
    'catalog.request',
    'catalog.request_for_others',
    'catalog.manage',
    'catalog.view_agent_only_items',
  ],
  approval: ['approval.view', 'approval.act', 'approval.act_as_delegate', 'approval.manage', 'approval.override'],

  // --- Knowledge base ------------------------------------------------------
  kb: [
    'kb.view',
    'kb.view_drafts',
    'kb.create',
    'kb.edit',
    'kb.edit_others',
    'kb.delete',
    'kb.publish',
    'kb.approve',
    'kb.manage_categories',
    'kb.view_analytics',
  ],

  // --- Assets and CMDB -----------------------------------------------------
  asset: [
    'asset.view',
    'asset.create',
    'asset.edit',
    'asset.delete',
    'asset.assign',
    'asset.import',
    'asset.export',
    'asset.manage_types',
    'asset.view_financials',
    'asset.edit_financials',
  ],
  cmdb: ['cmdb.view', 'cmdb.edit_relationships', 'cmdb.manage_ci_types', 'cmdb.view_impact'],
  /**
   * The software inventory: titles, where each is installed, and the CSV
   * import that fills it from a discovery tool's export. Separate from
   * `asset.*` because who may curate the software catalogue is a different
   * question from who may hand a laptop to somebody.
   */
  software: ['software.view', 'software.create', 'software.edit', 'software.delete', 'software.import'],
  contract:['contract.view', 'contract.create', 'contract.edit', 'contract.delete', 'contract.approve'],
  purchase: ['purchase.view', 'purchase.create', 'purchase.edit', 'purchase.delete', 'purchase.approve', 'purchase.receive'],

  // --- Identity ------------------------------------------------------------
  user: [
    'user.view',
    'user.create',
    'user.edit',
    'user.delete',
    'user.merge',
    'user.import',
    'user.export',
    'user.impersonate',
    'user.reset_password',
    'user.view_pii',
  ],
  agent: [
    'agent.view',
    'agent.create',
    'agent.edit',
    'agent.delete',
    'agent.manage_availability',
    'agent.view_workload',
    'agent.convert',
  ],
  group: ['group.view', 'group.create', 'group.edit', 'group.delete', 'group.manage_members'],
  org: [
    'org.department.view',
    'org.department.manage',
    'org.location.view',
    'org.location.manage',
    'org.workspace.view',
    'org.workspace.manage',
  ],

  // --- Configuration -------------------------------------------------------
  role: ['role.view', 'role.create', 'role.edit', 'role.delete', 'role.assign'],
  sla: ['sla.view', 'sla.manage', 'sla.business_hours.manage', 'sla.holiday.manage'],
  automation: [
    'automation.view',
    'automation.create',
    'automation.edit',
    'automation.delete',
    'automation.run_manually',
    'automation.view_execution_log',
  ],
  field: ['field.view', 'field.manage', 'field.form.manage', 'field.status.manage', 'field.priority.manage'],
  email: ['email.settings.view', 'email.settings.manage', 'email.template.manage', 'email.log.view', 'email.mailbox.manage'],
  notification: ['notification.template.manage', 'notification.view_own', 'notification.manage_preferences'],
  view: ['view.create_personal', 'view.create_shared', 'view.edit_shared', 'view.delete_shared', 'view.manage_default'],

  // --- Reporting -----------------------------------------------------------
  report: [
    'report.view',
    'report.create',
    'report.edit_others',
    'report.delete',
    'report.export',
    'report.schedule',
    'dashboard.view',
    'dashboard.create',
    'dashboard.edit_shared',
    'dashboard.manage_default',
  ],

  // --- Platform administration ---------------------------------------------
  admin: [
    'admin.settings.view',
    'admin.settings.manage',
    'admin.audit.view',
    'admin.api_key.view',
    'admin.api_key.manage',
    'admin.security.manage',
    'admin.sso.manage',
    'admin.session.manage',
    'admin.webhook.manage',
    'admin.job.view',
    'admin.job.manage',
    'admin.import',
    'admin.billing.view',
  ],
} as const;

export type PermissionGroup = keyof typeof PERMISSIONS;

export const ALL_PERMISSIONS = Object.values(PERMISSIONS).flat() as readonly string[];

export type Permission = (typeof PERMISSIONS)[PermissionGroup][number];

const PERMISSION_SET: ReadonlySet<string> = new Set(ALL_PERMISSIONS);

export function isPermission(value: string): value is Permission {
  return PERMISSION_SET.has(value);
}

export const PermissionSchema = z.string().refine(isPermission, {
  message: 'Unknown permission',
}) as unknown as z.ZodType<Permission>;

// ---------------------------------------------------------------------------
// Which permissions accept a scope
// ---------------------------------------------------------------------------

/**
 * Permissions on records that belong to somebody are scopable. Configuration
 * permissions are not — there is no "edit SLA policies, but only mine".
 * Anything absent from this set is implicitly `global` when granted.
 */
export const SCOPABLE_PERMISSIONS: ReadonlySet<string> = new Set<string>([
  'ticket.view',
  'ticket.edit',
  'ticket.delete',
  'ticket.assign',
  'ticket.reply',
  'ticket.note.view',
  'ticket.note.create',
  'ticket.merge',
  'ticket.split',
  'ticket.link',
  'ticket.bulk_edit',
  'ticket.export',
  'ticket.spam',
  'ticket.edit_requester',
  'ticket.time_entry.view',
  'ticket.time_entry.manage',
  'ticket.task.view',
  'ticket.task.manage',
  'problem.view',
  'problem.edit',
  'problem.delete',
  'problem.assign',
  'change.view',
  'change.edit',
  'change.delete',
  'change.assign',
  'release.view',
  'release.edit',
  'release.delete',
  'release.assign',
  'asset.view',
  'asset.edit',
  'asset.delete',
  'user.view',
  'user.edit',
  'agent.view',
  'report.view',
  'dashboard.view',
]);

export function isScopable(permission: string): boolean {
  return SCOPABLE_PERMISSIONS.has(permission);
}

/** A permission as stored on a role. */
export const RolePermissionSchema = z.object({
  permission: PermissionSchema,
  /** Ignored for non-scopable permissions; defaults to `global` there. */
  scope: ScopeSchema.default('global'),
});
export type RolePermission = z.infer<typeof RolePermissionSchema>;

// ---------------------------------------------------------------------------
// Human-readable metadata, used by the role editor
// ---------------------------------------------------------------------------

export const PERMISSION_GROUP_LABELS: Record<PermissionGroup, string> = {
  ticket: 'Tickets',
  problem: 'Problems',
  change: 'Changes',
  release: 'Releases',
  catalog: 'Service catalogue',
  approval: 'Approvals',
  kb: 'Knowledge base',
  asset: 'Assets',
  cmdb: 'CMDB',
  software: 'Software',
  contract: 'Contracts',
  purchase: 'Purchase orders',
  user: 'Requesters',
  agent: 'Agents',
  group: 'Groups',
  org: 'Organisation',
  role: 'Roles',
  sla: 'SLA and business hours',
  automation: 'Automation',
  field: 'Fields and forms',
  email: 'Email',
  notification: 'Notifications',
  view: 'Saved views',
  report: 'Reporting',
  admin: 'Administration',
};

/**
 * Permissions that grant meaningful power over the platform itself. The UI
 * warns before granting these, and the audit log flags every change to them.
 */
export const SENSITIVE_PERMISSIONS: ReadonlySet<string> = new Set<string>([
  'user.impersonate',
  'user.view_pii',
  'user.reset_password',
  'ticket.purge',
  'role.create',
  'role.edit',
  'role.assign',
  'admin.settings.manage',
  'admin.security.manage',
  'admin.sso.manage',
  'admin.api_key.manage',
  'admin.session.manage',
  'admin.webhook.manage',
  'approval.override',
  'change.override_blackout',
]);
